import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { getCollectionById, getCollectionPostcards, getCollectionCategoriesGrouped } from "../api/collections";
import { getImageRelationsByCategory } from "../api/image_relations";
import Spinner from "../components/Spinner";
import AppHeaderLayout from "../components/AppHeaderLayout";
import ChannelPostcardCard from '../components/Channel/ChannelPostcardCard';
import { useTranslation } from 'react-i18next';
import BackButton from '../components/Buttons/BackButton';
import MoreButton from '../components/Buttons/MoreButton';
import ChannelOptionsMenu from '../components/Menus/ChannelOptionsMenu';
import ChannelInfo from '../components/Channel/ChannelInfo';
import { iconButtonStyles } from '../styles/commonStyles';
import { BiMenu } from 'react-icons/bi';
import AddPostcardModal from '../components/Modals/AddPostcardModal';
import ChannelInfoModal from '../components/Modals/ChannelInfoModal';
import EditChannelModal from '../components/Modals/EditChannelModal';
import LeaveChannelModal from '../components/Modals/LeaveChannelModal';
import { Plus } from 'lucide-react';
import { useQuery as useRQ } from 'react-query';
import ChannelCategoriesGroups from '../components/Channel/ChannelCategoriesGroups';
import LanguageFilter from '../components/Common/LanguageFilter';
import { useSelector } from 'react-redux';
import fetchClient from '../utils/fetchClient';
import DeleteChannelModal from '../components/Modals/DeleteChannelModal';
import { getSavedLanguage, getSavedInterfaceLanguage } from '../utils/themeStorage';
import { useUserLevel } from '../hooks/useUserLevel';

function Channel() {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { t, i18n } = useTranslation();
    const { hasFeature } = useUserLevel();

    // Инициализация языка интерфейса
    useEffect(() => {
        const savedInterfaceLanguage = getSavedInterfaceLanguage();
        if (savedInterfaceLanguage !== i18n.language) {
            i18n.changeLanguage(savedInterfaceLanguage);
        }
    }, [i18n]);
    const [menuOpen, setMenuOpen] = useState(false);
    const [sidebarMenuOpen, setSidebarMenuOpen] = useState(false);
    const [infoModalOpen, setInfoModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [leaveModalOpen, setLeaveModalOpen] = useState(false);
    const [isAddPostcardOpen, setAddPostcardOpen] = useState(false);
    const [isWide, setIsWide] = useState(false);
    const userId = useSelector(state => state.auth.user?.id);
    const [isSubscribed, setIsSubscribed] = useState(null);
    const [subLoading, setSubLoading] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [showUnapproved, setShowUnapproved] = useState(false);  // Новое состояние
    const [switchingMode, setSwitchingMode] = useState(false);     // Состояние переключения режимов
    const [selectedLanguage, setSelectedLanguage] = useState(() => {
        // Загружаем сохраненный язык при инициализации
        const savedLanguage = getSavedLanguage();
        return savedLanguage === 'ru' || savedLanguage === 'en' ? savedLanguage : 'all';
    });  // Фильтр по языку

    const { data: channelData, isLoading, error } = useQuery(
        ['channel', id],
        () => getCollectionById(id),
        {
            enabled: !!id,
            staleTime: 5 * 60 * 1000, // 5 минут - данные считаются свежими
            cacheTime: 10 * 60 * 1000, // 10 минут - кеш хранится в памяти
            refetchOnWindowFocus: false, // Не перезагружать при фокусе окна
            refetchOnMount: false // Не перезагружать при монтировании компонента
        }
    );

    // Получаем выбранную категорию из query
    const params = new URLSearchParams(location.search);
    const selectedCategoryId = params.get('category');

    // Сбрасываем состояние показа неодобренных при смене категории
    useEffect(() => {
        setShowUnapproved(false);
    }, [selectedCategoryId]);

    // Получаем открытки с учётом фильтрации по категории и языку
    const { data: postcards = [], isLoading: loadingPostcards } = useQuery(
        ['postcards', id, selectedCategoryId, showUnapproved, selectedLanguage],  // Добавляем selectedLanguage в ключ
        () => {
            if (selectedCategoryId) {
                return getImageRelationsByCategory(id, selectedCategoryId, showUnapproved, selectedLanguage);
            } else {
                return getCollectionPostcards(id, selectedLanguage);
            }
        },
        {
            enabled: !!id,
            staleTime: 5 * 60 * 1000, // 5 минут - данные считаются свежими
            cacheTime: 10 * 60 * 1000, // 10 минут - кеш хранится в памяти
            refetchOnWindowFocus: false, // Не перезагружать при фокусе окна
            refetchOnMount: false // Не перезагружать при монтировании компонента
        }
    );

    // Получаем группы и категории для канала
    const { data: groupedCategories, isLoading: loadingGroups } = useRQ(
        ['collectionCategoriesGrouped', id],
        () => getCollectionCategoriesGrouped(id),
        {
            enabled: !!id,
            staleTime: 5 * 60 * 1000, // 5 минут - данные считаются свежими
            cacheTime: 10 * 60 * 1000, // 10 минут - кеш хранится в памяти
            refetchOnWindowFocus: false, // Не перезагружать при фокусе окна
            refetchOnMount: false // Не перезагружать при монтировании компонента
        }
    );

    // Проверяем подписку
    useEffect(() => {
        if (!userId || !id) return;
        setIsSubscribed(null);
        fetchClient.get(`/subscriptions/?user_id=${userId}&subscriber_id=${id}`)
            .then(res => {
                setIsSubscribed(Array.isArray(res) ? res.length > 0 : false);
            })
            .catch(() => setIsSubscribed(false));
    }, [userId, id]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.code === 'Insert') {
                setAddPostcardOpen(true);
            }
            if (window.innerWidth > 640 && (e.key === 'i' || e.key === 'ш')) {
                setInfoModalOpen(true);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    useEffect(() => {
        const onResize = () => {
            setIsWide(window.innerWidth > 1166);
        };
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const handleMenuItemClick = (action) => {
        if (action === "leaveChannel") {
            setLeaveModalOpen(true);
        } else if (action === "channelDescription") {
            setInfoModalOpen(true);
        } else if (action === "addPostcard") {
            setAddPostcardOpen(true);
        } else if (action === "deleteChannel") {
            setDeleteModalOpen(true);
        }
        setMenuOpen(false);
    };

    const handleLeaveChannel = () => {
        setLeaveModalOpen(false);
        navigate('/mychannels');
    };

    const handleSubscribe = async () => {
        if (!userId || !id) return;
        setSubLoading(true);
        try {
            await fetchClient.post(`/subscriptions/`, { user_id: userId, subscriber_id: id });
            setIsSubscribed(true);
        } catch (e) {
            // Можно добавить тост/уведомление об ошибке
        }
        setSubLoading(false);
    };

    const handleDeleteChannel = async () => {
        try {
            await fetchClient.delete(`/collections/${id}`);
            queryClient.invalidateQueries(['collections']);
            navigate('/mychannels');
        } catch (e) {
            alert(e.message || 'Ошибка удаления канала');
        }
        setDeleteModalOpen(false);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Spinner />
            </div>
        );
    }

    if (error) {
        return <div>Error loading channel data</div>;
    }

    const leftContent = (
        <div className="flex items-center gap-2">
            <button className={iconButtonStyles + " block md:hidden mr-2"} onClick={() => setSidebarMenuOpen(true)} aria-label="Открыть меню">
                <BiMenu className="text-2xl" />
            </button>
            {/* Показываем кнопку "Назад" только если это не автоматический канал */}
            {!channelData?.is_default && <BackButton onClick={() => navigate('/mychannels')} />}
            <ChannelInfo
                title={channelData?.name}
                subscribersCount={channelData?.subscribers_count || 0}
                postcardsCount={channelData?.postcards_count || 0}
                onAvatarClick={() => {
                    // Если пользователь - владелец канала, показываем модальное окно редактирования
                    if (userId && String(userId) === String(channelData?.user_id)) {
                        setEditModalOpen(true);
                    } else {
                        // Иначе показываем информацию о канале
                        setInfoModalOpen(true);
                    }
                }}
            />
            {isSubscribed === false && userId && String(userId) !== String(channelData?.user_id) && (
                <button
                    className="hidden md:block ml-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                    onClick={handleSubscribe}
                    disabled={subLoading}
                >
                    {subLoading ? '...' : 'Подписаться'}
                </button>
            )}
        </div>
    );
    const rightContent = (
        <div className="relative flex items-center gap-2">
            {/* Фильтр по языку - показываем только на десктопе и если функция включена */}
            {hasFeature('GALLERY_LANG') && (
                <div className="hidden md:flex">
                    <LanguageFilter
                        selectedLanguage={selectedLanguage}
                        onLanguageChange={setSelectedLanguage}
                        size="sm"
                    />
                </div>
            )}
            <MoreButton onClick={() => setMenuOpen((v) => !v)} />
            <ChannelOptionsMenu
                isOpen={menuOpen}
                onSelect={handleMenuItemClick}
                onClose={() => setMenuOpen(false)}
                openEditModal={() => setEditModalOpen(true)}
                userId={userId}
                ownerId={channelData?.user_id}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
            />
        </div>
    );

    return (
        <AppHeaderLayout
            leftContent={leftContent}
            rightContent={rightContent}
            sidebarMenuOpen={sidebarMenuOpen}
            onSidebarMenuOpen={() => setSidebarMenuOpen(true)}
            onSidebarMenuClose={() => setSidebarMenuOpen(false)}
        >
            <div className="p-4 pb-20">
                {/* Заголовок с информацией о категории */}
                {selectedCategoryId && (
                    <div className="mb-4 text-center">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                            Категория: {groupedCategories?.groups?.find(g => 
                                g.categories?.some(c => c.id == selectedCategoryId)
                            )?.categories?.find(c => c.id == selectedCategoryId)?.name || 'Неизвестная категория'}
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {switchingMode ? '⏳ Переключение режима...' : (
                                showUnapproved ? '🔍 Режим просмотра: Все открытки' : '✅ Режим просмотра: Только одобренные'
                            )}
                        </p>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 rounded-lg">
                            💡 <strong>Совет:</strong> Используйте кнопку ниже для переключения между режимами просмотра. 
                            В режиме "Все открытки" вы увидите статус модерации каждой открытки.
                        </div>
                    </div>
                )}
                
                
                
                <ChannelCategoriesGroups groupedCategories={groupedCategories} loading={loadingGroups} />
                
                {/* Кнопка переключения между одобренными и неодобренными */}
                {selectedCategoryId && (
                    <div className="mb-4 flex flex-col items-center gap-2">
                        <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
                            <div className="mb-1">
                                Найдено открыток: <span className="font-semibold text-lg">
                                    {switchingMode ? '...' : postcards.length}
                                </span>
                                {showUnapproved ? ' (включая неодобренные)' : ' (только одобренные)'}
                            </div>
                            <div className="text-xs">
                                {switchingMode ? '⏳ Обновление...' : (
                                    showUnapproved ? '🔍 Режим: Все открытки (включая неодобренные)' : '✅ Режим: Только одобренные открытки'
                                )}
                            </div>
                        </div>
                        
                        {/* Статистика по статусам */}
                        {showUnapproved && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-lg">
                                {switchingMode ? (
                                    <div className="text-center">⏳ Обновление статистики...</div>
                                ) : (
                                    <div className="flex gap-4 justify-center">
                                        <span>✅ Одобренные: {postcards.filter(p => p.is_approved && !p.is_banned).length}</span>
                                        <span>⏳ На модерации: {postcards.filter(p => !p.is_approved && !p.is_banned).length}</span>
                                        <span>🚫 Забаненные: {postcards.filter(p => p.is_banned).length}</span>
                                    </div>
                                )}
                            </div>
                        )}
                        
                        <button
                            onClick={() => {
                                setSwitchingMode(true);
                                setShowUnapproved(!showUnapproved);
                                // Сбрасываем состояние загрузки после небольшой задержки
                                setTimeout(() => setSwitchingMode(false), 500);
                            }}
                            disabled={switchingMode}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                showUnapproved 
                                    ? 'bg-orange-500 text-white hover:bg-orange-600' 
                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                            } ${switchingMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {switchingMode ? '⏳ Переключение...' : (
                                showUnapproved ? '👁️ Показать одобренные' : '👁️ Показать все (включая неодобренные)'
                            )}
                        </button>
                    </div>
                )}
                
                {loadingPostcards || switchingMode ? (
                    <Spinner />
                ) : postcards.length === 0 ? (
                    <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                        {switchingMode ? (
                            <div>
                                <div className="text-lg font-semibold mb-2">⏳ Переключение режима...</div>
                                <div className="text-sm">Пожалуйста, подождите</div>
                            </div>
                        ) : selectedCategoryId ? (
                            showUnapproved ? (
                                <div>
                                    <div className="text-lg font-semibold mb-2">В категории нет неодобренных открыток</div>
                                    <div className="text-sm">Все открытки уже прошли модерацию или были удалены</div>
                                </div>
                            ) : (
                                <div>
                                    <div className="text-lg font-semibold mb-2">В категории нет одобренных открыток</div>
                                    <div className="text-sm">Попробуйте включить показ всех открыток (включая неодобренные)</div>
                                </div>
                            )
                        ) : (
                            <div>
                                <div className="text-lg font-semibold mb-2">Открыток нет</div>
                                <div className="text-sm">В этом канале пока нет открыток</div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div
                        className={isWide ? 'grid gap-4' : 'grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'}
                        style={isWide ? { gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 250px))' } : {}}
                    >
                        {postcards.map(postcard => (
                            <ChannelPostcardCard
                                key={postcard.id}
                                postcard={postcard}
                                imageUrl={postcard.image_url || postcard.imageUrl}
                                onImageClick={() => navigate(
                                    `/mychannels/${postcard.collection_id}/${postcard.id}`
                                )}
                                onDeleted={(deletedId) => {
                                    // Инвалидируем список открыток для обновления
                                    queryClient.invalidateQueries(['postcards', id, selectedCategoryId]);
                                }}
                            />
                        ))}
                    </div>
                )}
                {isSubscribed === false && userId && String(userId) !== String(channelData?.user_id) && (
                    <button
                        className="fixed bottom-4 left-4 right-4 z-50 block md:hidden w-auto px-4 py-3 bg-blue-600 text-white rounded-lg shadow-lg text-lg font-semibold hover:bg-blue-700 transition"
                        onClick={handleSubscribe}
                        disabled={subLoading}
                    >
                        {subLoading ? '...' : 'Подписаться'}
                    </button>
                )}
            </div>
            <AddPostcardModal
                isOpen={isAddPostcardOpen}
                onClose={() => setAddPostcardOpen(false)}
                channelId={id}
            />
            <ChannelInfoModal
                isOpen={infoModalOpen}
                onClose={() => setInfoModalOpen(false)}
                channelId={id}
            />
            <EditChannelModal
                isOpen={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                channelId={id}
                channelData={channelData}
            />
            <LeaveChannelModal
                isOpen={leaveModalOpen}
                onClose={() => setLeaveModalOpen(false)}
                onLeave={handleLeaveChannel}
                channelTitle={channelData?.name}
            />
            <DeleteChannelModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onDelete={handleDeleteChannel}
                channelTitle={channelData?.name}
            />
        </AppHeaderLayout>
    );
}

export default Channel;
